import React from 'react';
import styled from 'styled-components';
import Badge from 'components/Badge';
import Card from 'components/Card';
import InfoCard from 'components/InfoCard';

const PageContainer = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1f2937')};
`;

const Description = styled.p`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin-bottom: 24px;
  line-height: 1.6;
`;

const ExampleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
`;

const CodeBlock = styled.pre`
  background: ${(props) =>
    props.theme.name === 'dark' ? '#1E293B' : '#F1F5F9'};
  padding: 16px;
  border-radius: 8px;
  font-size: 13px;
  overflow-x: auto;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
  margin-top: 16px;
`;

const InfoCardPage = () => {
  return (
    <PageContainer>
      <Section>
        <SectionTitle>InfoCard Component</SectionTitle>
        <Description>
          InfoCard is a themed component for displaying label/value pairs in a
          compact, readable format. Perfect for sidebars and summary sections on
          detail pages. It supports theme-aware styling for both light and dark
          modes.
        </Description>
      </Section>

      <Section>
        <SectionTitle>Basic Usage</SectionTitle>
        <Description>
          Use InfoCard with InfoCard.Row to display key-value pairs. Values can
          be strings, numbers, or any renderable content.
        </Description>

        <ExampleGrid>
          <Card>
            <InfoCard>
              <InfoCard.Row label="Status" value="Active" />
              <InfoCard.Row label="Created" value="Jan 1, 2025" />
              <InfoCard.Row label="Total Items" value={42} />
              <InfoCard.Row label="Owner" value="John Doe" />
            </InfoCard>
          </Card>
        </ExampleGrid>

        <CodeBlock>
          {`import InfoCard from 'components/InfoCard';

<InfoCard>
  <InfoCard.Row label="Status" value="Active" />
  <InfoCard.Row label="Created" value="Jan 1, 2025" />
  <InfoCard.Row label="Total Items" value={42} />
  <InfoCard.Row label="Owner" value="John Doe" />
</InfoCard>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>With Title</SectionTitle>
        <Description>
          Add a title prop to display a header at the top of the InfoCard.
        </Description>

        <ExampleGrid>
          <Card>
            <InfoCard title="Order Summary">
              <InfoCard.Row label="Subtotal" value="$89.99" />
              <InfoCard.Row label="Tax" value="$7.20" />
              <InfoCard.Row label="Shipping" value="Free" />
              <InfoCard.Row label="Total" value="$97.19" />
            </InfoCard>
          </Card>
        </ExampleGrid>

        <CodeBlock>
          {`<InfoCard title="Order Summary">
  <InfoCard.Row label="Subtotal" value="$89.99" />
  <InfoCard.Row label="Tax" value="$7.20" />
  <InfoCard.Row label="Shipping" value="Free" />
  <InfoCard.Row label="Total" value="$97.19" />
</InfoCard>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Custom Value Rendering</SectionTitle>
        <Description>
          Pass children to InfoCard.Row instead of value for custom rendering,
          such as badges, links, or other components.
        </Description>

        <ExampleGrid>
          <Card>
            <InfoCard title="User Details">
              <InfoCard.Row label="Name" value="Jane Smith" />
              <InfoCard.Row label="Email" value="jane@example.com" />
              <InfoCard.Row label="Status">
                <Badge variant="success" size="small">
                  Active
                </Badge>
              </InfoCard.Row>
              <InfoCard.Row label="Role">
                <Badge variant="info" size="small">
                  Admin
                </Badge>
              </InfoCard.Row>
            </InfoCard>
          </Card>
        </ExampleGrid>

        <CodeBlock>
          {`import Badge from 'components/Badge';

<InfoCard title="User Details">
  <InfoCard.Row label="Name" value="Jane Smith" />
  <InfoCard.Row label="Email" value="jane@example.com" />
  <InfoCard.Row label="Status">
    <Badge variant="success" size="small">Active</Badge>
  </InfoCard.Row>
  <InfoCard.Row label="Role">
    <Badge variant="info" size="small">Admin</Badge>
  </InfoCard.Row>
</InfoCard>`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Props Reference</SectionTitle>

        <Description>
          <strong>InfoCard Props:</strong>
        </Description>
        <CodeBlock>
          {`{
  title?: string,      // Optional title displayed at the top
  children: node,      // InfoCard.Row components
  className?: string,  // Additional className
  style?: object,      // Inline styles
}`}
        </CodeBlock>

        <Description style={{ marginTop: '24px' }}>
          <strong>InfoCard.Row Props:</strong>
        </Description>
        <CodeBlock>
          {`{
  label: string,       // Label text displayed on the left (required)
  value?: string | number | node,  // Value displayed on the right
  children?: node,     // Custom content instead of value
}`}
        </CodeBlock>
      </Section>
    </PageContainer>
  );
};

export default InfoCardPage;
